import type { ObjectiveReminderValue } from '@/constants/ObjectiveReminder';

import { ObjectiveReminder } from '@/constants/ObjectiveReminder';
import { parseIsoDate } from '@/utils/dateUtils';

const TWO_WEEKS_IN_DAYS = 14;

/** Every reminder, from the earliest to the latest. */
export const OBJECTIVE_REMINDERS = Object.values(ObjectiveReminder);

/** The same day `months` months earlier, or the last day of that month when it is shorter. */
const monthsBefore = (date: Date, months: number): Date => {
    const year = date.getFullYear();
    const month = date.getMonth() - months;
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    return new Date(year, month, Math.min(date.getDate(), lastDayOfMonth));
};

/** The day `reminder` falls on before `endDate`, counted the way the server sends it. */
export const reminderDate = (reminder: ObjectiveReminderValue, endDate: Date): Date => {
    switch (reminder) {
        case ObjectiveReminder.TWO_MONTHS_BEFORE_END:
            return monthsBefore(endDate, 2);
        case ObjectiveReminder.ONE_MONTH_BEFORE_END:
            return monthsBefore(endDate, 1);
        case ObjectiveReminder.TWO_WEEKS_BEFORE_END:
            return new Date(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate() - TWO_WEEKS_IN_DAYS,
            );
    }
};

/**
 * Whether `reminder` can still be scheduled on `today` for an objective ending on `endDate`
 * (`yyyy-MM-dd`): not without an end date, nor once the reminder's day has passed.
 */
export const canScheduleReminder = (
    reminder: ObjectiveReminderValue,
    endDate: string,
    today: Date,
): boolean => {
    const end = parseIsoDate(endDate);
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return !!end && reminderDate(reminder, end) >= startOfToday;
};
